import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { ProfileImageComponent } from 'src/app/components/profile-image/profile-image.component';
import { SharedUsersComponent } from 'src/app/components/shared-users/shared-users.component';
import * as myGlobals from 'src/app/globals';
import { ShareManagementResponseModal, ShareManagementUpdatedModal } from 'src/app/models/modal.model';
import { TrimStringPipe } from 'src/app/pipes/trim-string.pipe';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { NotificationService } from 'src/app/services/notification.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { ShareManagementModalService } from './share-management-modal.service';
@Component({
  selector: 'app-share-management-modal',
  standalone: true,
  imports: [ModalComponent, CommonModule, ReactiveFormsModule, FormsModule, ProfileImageComponent, SharedUsersComponent, TranslateModule],
  providers: [ModalService, ShareManagementModalService, TrimStringPipe],
  templateUrl: './share-management-modal.component.html',
  styleUrls: ['./share-management-modal.component.scss'],
})
export class ShareManagementModalComponent implements OnInit, OnDestroy {
  @Input() modalOpen!: boolean;
  subscription: Subscription = new Subscription();
  itemSubscription: Subscription = new Subscription();
  @ViewChild('btnCancelRef') btnCancelRef!: ElementRef;

  //string
  modalTitle = this.commonService.getTranslatedMessage('MANAGE_SHARING');
  modalId = 'shareManagementModal';
  shareNote_ = '';
  sharePermisionForm!: FormGroup;

  //data
  removedItems: any[] = [];
  updatedItems: any[] = [];
  sharedItems: ShareManagementResponseModal[] = [];
  userImageUrls: { [key: string]: string } = {};
  groupImageUrls: { [key: string]: string } = {};
  updatedEncryptedGroups: any[] = [];
  updatedEncryptedUsers: any[] = [];
  deletedEncryptedUsers: string[] = [];
  deletedEncryptedGroups: string[] = [];
  selectedItem: any;

  //boolean
  submitted = false;
  modalOut = false;
  openModal = false;
  hasRtukUrl = myGlobals.Config.hasRtukUrl;
  isBulkShares?: boolean;
  tempObjectId = '';

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    const modalContainer = document.getElementById(this.modalId);
    const isModalClicked = targetElement?.contains(modalContainer);
    if (isModalClicked) {
      this.modalOut = true;
      this.submitted = false;
    }
  }

  constructor(
    private fb: FormBuilder,
    private modalService: ModalService,
    private shareManagementModalService: ShareManagementModalService,
    public commonService: CommonService,
    public trimStringPipe: TrimStringPipe,
    private notificationService: NotificationService,
  ) {
    this.createSharePermissionForm();
  }

  ngOnInit(): void {
    this.itemSubscription.add(
      this.commonService.sharesMultipleSelectedItems$.subscribe((item) => {
        if (item && item.length === 1) {
          [this.selectedItem] = item;
          if (this.selectedItem.multipleObject) {
            this.getSharesListForBulkShare(this.selectedItem.objectId);
          } else {
            this.getSharedList(this.selectedItem.objectId);
          }
        }
      }),
    );
    this.itemSubscription.add(
      this.commonService.multipleSelectedItems$.subscribe((item) => {
        if (item && item.length === 1) {
          [this.selectedItem] = item;
          this.getSharedList(this.selectedItem.objectId);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.itemSubscription) {
      this.itemSubscription.unsubscribe();
    }
  }

  getSharedList(id: string): void {
    this.shareManagementModalService.getSharesList(id).subscribe({
      next: (res) => {
        this.sharedItems = res.content;
      },
      error: () => {
        this.sharedItems = [];
      },
    });
  }

  getSharesListForBulkShare(id: string): void {
    this.shareManagementModalService.getSharesListForBulkShare(id).subscribe({
      next: (res) => {
        this.sharedItems = res.content;
      },
      error: () => {
        this.sharedItems = [];
      },
    });
  }

  createSharePermissionForm(): void {
    this.sharePermisionForm = this.fb.group({
      permission: new FormControl(),
      note: new FormControl(),
    });
  }

  changePermission(item: ShareManagementResponseModal): void {
    this.checkIfItemIncludes(item);
  }

  toggleNote(item: ShareManagementResponseModal): void {
    item.showInput = !item.showInput;
    this.checkIfItemIncludes(item);
  }

  removeItem(item: ShareManagementResponseModal): void {
    const removedItems = {
      shareTargetId: item.shareTargetId,
    };
    this.removedItems.push(removedItems);
    this.sharedItems = this.sharedItems.filter((i: any) => {
      if (item.shareTargetId === i.shareTargetId) {
        return false;
      }

      return true;
    });
    if (item.groupShare && item.groupInfoDto) {
      const teamSharingRequest: any = {
        teamId: item.groupInfoDto?.id,
        permission: item.permission,
        multipleObject: item.multipleObject,
        privateShareId: item.privateShareId,
      };
      this.deletedEncryptedGroups.push(teamSharingRequest);
    } else if (item.userInfoDto) {
      const individualSharingRequest: any = {
        email: item.userInfoDto?.email,
        permission: item.permission,
        multipleObject: item.multipleObject,
        privateShareId: item.privateShareId,
      };
      this.deletedEncryptedUsers.push(individualSharingRequest);
    }
  }

  async deleteItems(items: any, deleteHandler: (encryptedId: string, shared: string) => Promise<boolean>): Promise<boolean> {
    const encryptedId = this.selectedItem && this.selectedItem.encryptedId;
    const sharedTo: string[] | null = items;

    if (!sharedTo || sharedTo.length === 0) {
      return true;
    }
    const results = await Promise.all(sharedTo.map((shared) => deleteHandler(encryptedId, shared)));

    return results.every((result) => result);
  }

  async deleteUsers(users: any): Promise<boolean> {
    return this.deleteItems(users, this.deleteIndividualSharingHandler.bind(this));
  }

  async deleteGroups(groups: any): Promise<boolean> {
    return this.deleteItems(groups, this.deleteTeamSharingHandler.bind(this));
  }

  async updateUsers(users: any): Promise<boolean> {
    return this.updateItems(users, this.updateIndividualSharingHandler.bind(this));
  }

  async updateGroups(groups: any): Promise<boolean> {
    return this.updateItems(groups, this.updateTeamSharingHandler.bind(this));
  }

  async updateItems(items: any, updateHandler: (encryptedId: string, sharingRequest: any) => Promise<boolean>): Promise<boolean> {
    const encryptedId = this.selectedItem && this.selectedItem.encryptedId;
    const sharedTo: string[] | null = items;

    if (!sharedTo || sharedTo.length === 0) {
      return true;
    }

    const results = await Promise.all(sharedTo.map((sharingRequest) => updateHandler(encryptedId, sharingRequest)));

    return results.every((result) => result);
  }

  async deleteIndividualSharingHandler(encryptedId: string, shared: any): Promise<boolean> {
    return await SgeCrypto.deleteIndividualSharing(shared.multipleObject ? shared.privateShareId : encryptedId, shared.email);
  }

  async deleteTeamSharingHandler(encryptedId: string, shared: any): Promise<boolean> {
    return await SgeCrypto.deleteTeamSharing(shared.multipleObject ? shared.privateShareId : encryptedId, shared.teamId);
  }

  async updateIndividualSharingHandler(encryptedId: string, individualSharingRequest: any): Promise<boolean> {
    return await SgeCrypto.updateIndividualSharing(individualSharingRequest.multiple ? individualSharingRequest.privateShareId : encryptedId, individualSharingRequest);
  }

  async updateTeamSharingHandler(encryptedId: string, teamSharingRequest: any): Promise<boolean> {
    return await SgeCrypto.updateTeamSharing(teamSharingRequest.multiple ? teamSharingRequest.privateShareId : encryptedId, teamSharingRequest);
  }

  checkIfItemIncludes(item: ShareManagementResponseModal): void {
    const existingItem = this.updatedItems.find((i) => item.shareTargetId === i.shareTargetId);

    if (!existingItem) {
      this.updatedItems.push(item);
    }

    if (item.groupShare) {
      const teamSharingRequest: any = {
        teamId: item.groupInfoDto?.id,
        permission: item.permission,
        multiple: item.multipleObject,
        privateShareId: item.privateShareId,
      };
      this.updatedEncryptedGroups.push(teamSharingRequest);
    } else {
      const individualSharingRequest: any = {
        email: item.userInfoDto?.email,
        permission: item.permission,
        multiple: item.multipleObject,
        privateShareId: item.privateShareId,
      };
      this.updatedEncryptedUsers.push(individualSharingRequest);
    }
  }

  async encryptedSubmit(event: Event): Promise<void> {
    event.preventDefault();

    let success = true;
    if (this.deletedEncryptedUsers.length > 0) success = success && (await this.deleteUsers(this.deletedEncryptedUsers));
    if (this.deletedEncryptedGroups.length > 0) success = success && (await this.deleteGroups(this.deletedEncryptedGroups));
    if (this.updatedEncryptedGroups.length > 0) success = success && (await this.updateGroups(this.updatedEncryptedGroups));
    if (this.updatedEncryptedUsers.length > 0) success = success && (await this.updateUsers(this.updatedEncryptedUsers));

    if (success) {
      this.onSubmit();
    } else {
      const notification = {
        message: this.commonService.getTranslatedMessage('ERROR_OCCURRED'),
      };
      this.notificationService.errorNotification(notification);
    }
  }

  onSubmit(): void {
    const newUpdatedList: ShareManagementUpdatedModal[] = [];
    this.updatedItems.forEach((item) => {
      const updatedItem = {
        permission: typeof item.permission !== 'undefined' ? item.permission : 'VIEW',
        note: typeof item.shareNote !== 'undefined' ? item.shareNote : '',
        shareTargetId: item.shareTargetId,
      };
      newUpdatedList.push(updatedItem);
    });
    const param = {
      permissionAndNoteUpdateDtos: newUpdatedList,
      removedShareTargets: this.removedItems,
    };
    this.shareManagementModalService.sendRemovedAndChangedList(param).subscribe({
      next: () => {
        this.removedItems = [];
        this.updatedItems = [];
        this.commonService.rebindListener.next();
        if (this.sharedItems.length === 0) this.commonService.cancelShares.next(true);
        const notification = {
          message: this.commonService.getTranslatedMessage('OPERATION_SUCCESSFUL'),
        };
        this.notificationService.successNotification(notification);
      },
    });
    if (this.updatedItems.length || this.removedItems.length) {
      this.submitted = true;
      this.modalService.closeModalManually(this.modalId);
      this.commonService.setOpenedModalId('');
      this.btnCancelRef.nativeElement.click();
    }
  }

  closeModalButtonTrigger(): void {
    if (this.submitted) {
      this.updatedItems = [];
      this.removedItems = [];
      this.modalOpen = false;
    }
    this.removedItems = [];
    this.updatedItems = [];
    this.modalService.closeModalManually(this.modalId);
  }

  getGroupName(group: any): string {
    return group.name;
  }
}
